import { useState, useCallback, useMemo } from 'react'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { ContentSection } from './components/ContentSection'
import { useI18n } from './i18n/index.tsx'
import {
  Copy, Check, Trash2, Type, ArrowDown
} from 'lucide-react'

type CaseType = 'upper' | 'lower' | 'title' | 'sentence' | 'camel' | 'pascal' | 'snake' | 'kebab' | 'constant' | 'alternating' | 'reverse'

interface CaseOption {
  key: CaseType
  labelKey: string
  label: string
}

const CASE_OPTIONS: CaseOption[] = [
  { key: 'upper', labelKey: 'caseUpper', label: 'UPPER CASE' },
  { key: 'lower', labelKey: 'caseLower', label: 'lower case' },
  { key: 'title', labelKey: 'caseTitle', label: 'Title Case' },
  { key: 'sentence', labelKey: 'caseSentence', label: 'Sentence case' },
  { key: 'camel', labelKey: 'caseCamel', label: 'camelCase' },
  { key: 'pascal', labelKey: 'casePascal', label: 'PascalCase' },
  { key: 'snake', labelKey: 'caseSnake', label: 'snake_case' },
  { key: 'kebab', labelKey: 'caseKebab', label: 'kebab-case' },
  { key: 'constant', labelKey: 'caseConstant', label: 'CONSTANT_CASE' },
  { key: 'alternating', labelKey: 'caseAlternating', label: 'aLtErNaTiNg' },
  { key: 'reverse', labelKey: 'caseReverse', label: 'esreveR' },
]

function toTitleCase(str: string): string {
  return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
}

function toSentenceCase(str: string): string {
  return str.toLowerCase().replace(/(^\w|[.!?]\s+\w)/g, c => c.toUpperCase())
}

function toCamelCase(str: string): string {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => index === 0 ? word.toLowerCase() : word.toUpperCase())
    .replace(/\s+/g, '')
}

function toPascalCase(str: string): string {
  return str
    .replace(new RegExp(/[-_]+/, 'g'), ' ')
    .replace(new RegExp(/(?:^\w|[A-Z]|\b\w)/, 'g'), (word) => word.toUpperCase())
    .replace(/\s+/g, '')
}

function toSnakeCase(str: string): string {
  return str.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)?.map(s => s.toLowerCase()).join('_') || ''
}

function toKebabCase(str: string): string {
  return str.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)?.map(s => s.toLowerCase()).join('-') || ''
}

function convertCase(text: string, caseType: CaseType): string {
  switch (caseType) {
    case 'upper': return text.toUpperCase()
    case 'lower': return text.toLowerCase()
    case 'title': return toTitleCase(text)
    case 'sentence': return toSentenceCase(text)
    case 'camel': return toCamelCase(text)
    case 'pascal': return toPascalCase(text)
    case 'snake': return toSnakeCase(text)
    case 'kebab': return toKebabCase(text)
    case 'constant': return toSnakeCase(text).toUpperCase()
    case 'alternating': return text.split('').map((c, i) => i % 2 === 0 ? c.toLowerCase() : c.toUpperCase()).join('')
    case 'reverse': return text.split('').reverse().join('')
    default: return text
  }
}

function countWords(text: string): number {
  return text.trim() === '' ? 0 : text.trim().split(/\s+/).length
}

function App() {
  const { t } = useI18n()
  const [input, setInput] = useState('')
  const [copied, setCopied] = useState<string | null>(null)

  const wordCount = useMemo(() => countWords(input), [input])
  const charCount = input.length
  const charCountNoSpace = input.replace(/\s/g, '').length
  const lineCount = input === '' ? 0 : input.split('\n').length

  const handleCopy = useCallback(async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(key)
      setTimeout(() => setCopied(null), 2000)
    } catch {}
  }, [])

  const handleClear = useCallback(() => setInput(''), [])

  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText()
      setInput(text)
    } catch {}
  }, [])

  return (
    <div className="min-h-screen bg-background flex flex-col transition-colors duration-200">
      <Header />

      {/* Hero */}
      <div className="text-center py-6 px-4">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
          {t('caseConverter.title') || 'Case Converter'}
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          {t('caseConverter.subtitle') || 'Convert text between UPPER, lower, Title, camelCase, snake_case, and more. '}
          <span className="text-primary font-medium">{t('caseConverter.privacy') || '100% client-side'}</span>.
        </p>
      </div>

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 pb-8 space-y-4">
        {/* Input */}
        <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Type className="w-4 h-4 text-brand-primary" />
              <span className="text-sm font-medium text-foreground">{t('caseConverter.inputLabel') || 'Input Text'}</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handlePaste} className="px-3 py-1.5 text-xs bg-background border border-border rounded-lg hover:bg-muted transition-colors">
                {t('actions.paste') || 'Paste'}
              </button>
              <button onClick={handleClear} disabled={!input} className="px-3 py-1.5 text-xs bg-background border border-border rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50">
                <Trash2 className="w-3.5 h-3.5 inline mr-1" />
                {t('actions.clear') || 'Clear'}
              </button>
            </div>
          </div>

          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={t('caseConverter.placeholder') || 'Type or paste text here...'}
            className="w-full h-40 px-4 py-3 bg-background border border-border rounded-lg text-foreground text-sm resize-y outline-none focus:ring-2 focus:ring-brand-primary/50 font-mono leading-relaxed"
            spellCheck={false}
          />

          {/* Stats */}
          <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
            <span>{t('caseConverter.words') || 'Words'}: <strong className="text-foreground">{wordCount}</strong></span>
            <span>{t('caseConverter.chars') || 'Chars'}: <strong className="text-foreground">{charCount}</strong></span>
            <span>{t('caseConverter.charsNoSpace') || 'No Spaces'}: <strong className="text-foreground">{charCountNoSpace}</strong></span>
            <span>{t('caseConverter.lines') || 'Lines'}: <strong className="text-foreground">{lineCount}</strong></span>
          </div>
        </div>

        {/* Output Grid */}
        {input && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {CASE_OPTIONS.map(option => {
              const result = convertCase(input, option.key)
              return (
                <div key={option.key} className="bg-surface rounded-xl shadow-sm border border-border p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {t(`caseConverter.${option.labelKey}`) || option.label}
                    </span>
                    <button
                      onClick={() => handleCopy(result, option.key)}
                      className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                    >
                      {copied === option.key ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  <div className="text-sm text-foreground font-mono break-all min-h-[1.5rem]">
                    {result}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {!input && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <ArrowDown className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">
              {t('caseConverter.emptyHint') || 'Type or paste text above to see all case conversions'}
            </p>
          </div>
        )}

        {/* Privacy Note */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">{t('security.note')}</p>
        </div>
      </main>

      <ContentSection />


      <Footer />
    </div>
  )
}

export default App
